import { useContext } from 'react';
import { AccountContext } from '@/context/account.context';
import { modalContext } from '@/context/modal.context';
import { toastContext } from '@/context/toast.context';
import Submission from '@/utils/api/odas/submission';
import { resourceHelpers } from '@/utils/helpers';
import useMixpanel from '@/hooks/useMixpanel';
import t from '@/utils/Language/UI-translations';

// Mirrors the Angular updateResourceAvailabilityModalController: always submits to the
// review queue (marked_unavailable/marked_available), never publishes immediately.
const useMarkUnavailable = () => {
  const account = useContext(AccountContext);
  const modal = useContext(modalContext);
  const { addToast } = useContext(toastContext);
  const { track } = useMixpanel();

  const openMarkUnavailableModal = (resource) => {
    if (!account?.state?.isLoggedIn) {
      modal.enqueueModal({ name: 'SignupModal' }, { forceOpen: true });
      return;
    }

    const isAvailable = !resourceHelpers.isCurrentlyUnavailable(resource);

    modal.enqueueModal(
      {
        name: 'ConfirmationModal',
        title: isAvailable ? t('Mark opportunity unavailable') : t('Mark opportunity available'),
        text: isAvailable
          ? t('Do you confirm that this opportunity is temporarily unavailable?')
          : t('Do you confirm that this opportunity is now available?'),
        confirmButtonText: isAvailable ? t('Yes, mark unavailable') : t('Yes, mark available'),
        declineButtonText: isAvailable ? t("No, it's available") : t("No, it's unavailable"),
        onConfirm: async () => {
          try {
            const response = await Submission.markResourceAvailability({ resource, available: !isAvailable });
            if (response.status === 200 || response.status === 201) {
              track(isAvailable ? 'Marked resource unavailable' : 'Marked resource available', {
                source: 'action menu',
                resource_id: resource.id,
                resource_type: resource.resource_type
              });
              addToast({
                message: t(
                  "Thank you for submitting edits! Your changes are pending until approved by One Degree staff. We'll notify you in about 1-3 days by email once your changes are approved."
                ),
                type: 'success'
              });
            } else {
              addToast({ message: t('There was a problem with your submission_'), type: 'error' });
            }
          } catch (error) {
            addToast({ message: t('There was a problem with your submission_'), type: 'error' });
          }
        }
      },
      { forceOpen: true }
    );
  };

  return { openMarkUnavailableModal };
};

export default useMarkUnavailable;
